/**
 * BLRRD Pointwise Filters — Sepia
 *
 * Fixed 3×3 colour matrix mapping RGB to warm brown tones. The
 * matrix is the standard Adobe Photoshop sepia formula (public,
 * documented in Microsoft's GDI+ ColorMatrix docs since 2002,
 * also published as the "Microsoft Sepia matrix" — same numbers):
 *
 *      [ 0.393  0.769  0.189 ]
 *      [ 0.349  0.686  0.168 ]
 *      [ 0.272  0.534  0.131 ]
 *
 * Origin: Microsoft's MSDN documentation for GDI+ ColorMatrix
 * effects (public since ~2002). Photoshop's "Image > Adjustments
 * > Photo Filter" + the original Sepia preset use identical
 * coefficients. NOT GIMP-sourced.
 *
 * Pipeline slot: POST-VIEW-TRANSFORM. Sepia is a display-referred
 * stylization — running on scene-linear ACEScg would push
 * highlights way beyond 1.0 in the warm tones. The matrix is
 * calibrated for sRGB-encoded input.
 *
 * Strength slider blends linearly between identity (0) and full
 * sepia (1). Strength = 0 → identity short-circuit.
 */

const M = new Float32Array([
    0.393, 0.769, 0.189,
    0.349, 0.686, 0.168,
    0.272, 0.534, 0.131,
]);

/** Default Sepia params. */
export function defaultSepiaParams() {
    return { strength: 1.0 };
}

/** True iff `params` represents the identity transform. */
export function isIdentitySepia(params) {
    if (!params) return true;
    const s = params.strength;
    if (typeof s !== 'number') return true;
    return s <= 0;
}

/**
 * Apply Sepia to a Float32 RGBA buffer in place.
 *
 * @param {Float32Array} rgba
 * @param {number}       w
 * @param {number}       h
 * @param {object}       params      { strength: 0..1 }
 * @param {object}       [opts]      { selectionMask? }
 * @returns {Float32Array}           same `rgba` reference
 */
export function applySepia(rgba, w, h, params, opts) {
    validateBuffer(rgba, w, h);
    if (isIdentitySepia(params)) return rgba;
    const mask = opts && opts.selectionMask ? opts.selectionMask : null;
    if (mask && mask.length !== w * h) {
        throw new Error('applySepia: selectionMask length mismatch');
    }
    const strength = params.strength > 1 ? 1 : params.strength;
    const inv = 1 - strength;
    const N = w * h;

    for (let p = 0; p < N; p++) {
        const i = p << 2;
        const m = mask ? mask[p] : 1;
        if (m <= 0) continue;
        const r = rgba[i], g = rgba[i + 1], b = rgba[i + 2];
        let sr = M[0] * r + M[1] * g + M[2] * b;
        let sg = M[3] * r + M[4] * g + M[5] * b;
        let sb = M[6] * r + M[7] * g + M[8] * b;
        /* Clamp to [0,1] — the matrix is calibrated for normalized
         * display-referred input but can overshoot on bright pixels. */
        if (sr > 1) sr = 1; else if (sr < 0) sr = 0;
        if (sg > 1) sg = 1; else if (sg < 0) sg = 0;
        if (sb > 1) sb = 1; else if (sb < 0) sb = 0;
        const newR = inv * r + strength * sr;
        const newG = inv * g + strength * sg;
        const newB = inv * b + strength * sb;
        if (m >= 1) {
            rgba[i]     = newR;
            rgba[i + 1] = newG;
            rgba[i + 2] = newB;
        } else {
            const invM = 1 - m;
            rgba[i]     = invM * r + m * newR;
            rgba[i + 1] = invM * g + m * newG;
            rgba[i + 2] = invM * b + m * newB;
        }
    }
    return rgba;
}

/** Exported for tests. */
export const SEPIA_MATRIX = M;

function validateBuffer(rgba, w, h) {
    if (!(rgba instanceof Float32Array)) {
        throw new TypeError('applySepia: rgba must be Float32Array');
    }
    if (rgba.length !== w * h * 4) {
        throw new Error('applySepia: rgba.length !== w*h*4');
    }
}
